import React, {
  ReactNode, useCallback, useId, useMemo,
} from 'react';

import Segmented, { SegmentedOption } from 'components/DesignSystem/Segmented';
import FilterField from 'components/Flux/Personalized/MyStudies/FilterBar/FilterField';
import {
  MyStudiesCompanyFilter,
  MyStudiesCreatorFilter,
} from 'types/Flux/Personalized/myStudiesFilters';

/** Os dois segmentos fixos: `criadoPor` 'me' ("Meus estudos") e 'empresa' ("Todos…"). */
export type CreatorSegment = Extract<MyStudiesCreatorFilter, string>;

/** Valor do segmentado quando um colega (`{ userId }`) está escolhido: nenhum segmento marcado. */
const NO_SEGMENT = '';
type SegmentValue = CreatorSegment | typeof NO_SEGMENT;

export interface CreatorFilterProps {
  /** `filters.criadoPor`. */
  value: MyStudiesCreatorFilter;
  /** Papel de quem vê a tela. */
  isAdmin: boolean;
  /** `filters.empresa`: com admin em Todas as empresas, o 2º segmento vira "Todos". */
  empresa: MyStudiesCompanyFilter;
  /** Recebe o segmento clicado. */
  onChange: (criadoPor: CreatorSegment) => void;
  /**
   * Posição reservada do segmento "Usuário ▾" (#2000), no grupo CRIADO POR, colada à direita dos
   * dois segmentos. O wrapper tem as classes do `Segmented` filled do DS: um
   * `<button className="ds-segmented-option active">` herda a altura, a borda, os cantos e o
   * estado ativo. A casca passa o `UserPopover` (#2000).
   */
  userSlot?: ReactNode;
}

/**
 * Filtro "Criado por" da barra de Meus Estudos (#1995): `Segmented` do DS com `variant='filled'`
 * (.dc.html:204-207). "Meus estudos" e "Todos da empresa"; para admin com Todas as empresas, o 2º
 * vira "Todos" (.dc.html:659). Com um colega escolhido (`{ userId }`), nenhum dos dois fica
 * marcado: quem mostra o colega é o "Usuário ▾" da #2000, no `userSlot`.
 *
 * O grupo nomeado "CRIADO POR" é o wrapper dos segmentos e do slot, para o "Usuário ▾" ser lido
 * como parte do mesmo controle; o `Segmented` fica sem nome próprio, dentro dele.
 *
 * Controlado por props: quem guarda o valor é o `useMyStudiesFilters` da tela.
 */
export default function CreatorFilter({
  value,
  isAdmin,
  empresa,
  onChange,
  userSlot,
}: CreatorFilterProps) {
  const labelId = useId();
  const allLabel = isAdmin && empresa === 'todas' ? 'Todos' : 'Todos da empresa';
  const options = useMemo<SegmentedOption<SegmentValue>[]>(() => [
    { value: 'me', label: 'Meus estudos' },
    { value: 'empresa', label: allLabel },
  ], [allLabel]);
  const selected: SegmentValue = typeof value === 'string' ? value : NO_SEGMENT;
  const hasUserSlot = Boolean(userSlot);

  const handleChange = useCallback((segment: SegmentValue) => {
    if (segment !== NO_SEGMENT) onChange(segment);
  }, [onChange]);

  return (
    <FilterField label="CRIADO POR" labelId={labelId} className="my-studies-creator-filter">
      <div role="group" aria-labelledby={labelId} className="my-studies-creator-filter-control">
        <Segmented
          variant="filled"
          fill={false}
          options={options}
          value={selected}
          onChange={handleChange}
        />
        {hasUserSlot && (
          <div className="ds-segmented ds-segmented-filled ds-segmented-auto my-studies-creator-filter-user">
            {userSlot}
          </div>
        )}
      </div>
    </FilterField>
  );
}
